<template>
  <section class="commission-archive" aria-label="Legacy commission archive">
    <h4>{{ $t('Commissions before January 1, 2025') }}</h4>
    <p>{{ $t('Only unpaid, unclaimed commissions are affected. Paid history stays intact.') }}</p>
    <p v-if="loading" role="status">{{ $t('Loading archive settings…') }}</p>
    <p v-else-if="error" role="alert">{{ error }}</p>
    <template v-if="archive">
      <p>{{ archive.first_tier.count + archive.second_tier.count }} {{ $t('commissions eligible for this batch') }}</p>
      <p v-if="!archive.enabled">{{ $t('Archive activation is pending review.') }}</p>
      <p v-else>{{ archive.restored ? $t('Restored: visible to this affiliate and eligible for payment.') : $t('Archived: hidden from this affiliate and excluded from payment.') }}</p>
      <button type="button" role="switch" :aria-checked="archive.restored ? 'true' : 'false'"
        :disabled="loading || saving || !archive.enabled" @click="toggleArchive">
        {{ saving ? $t('Saving…') : archive.restored ? $t('Archive pre-2025 commissions') : $t('Restore pre-2025 commissions') }}
      </button>
      <button type="button" :aria-expanded="showPreview ? 'true' : 'false'" :disabled="loading || saving"
        @click="togglePreview">{{ showPreview ? $t('Close pre-2025 preview') : $t('View all pre-2025 history') }}</button>
      <div v-if="showPreview" class="commission-archive__preview" role="region" aria-label="Pre-2025 commissions preview">
        <p>{{ $t('Preview all pre-2025 history, including paid and refunded records. Viewing does not restore commissions or change payout eligibility.') }}</p>
        <div class="commission-archive__filters">
          <label>{{ $t('Show') }} <select v-model="previewScope" :disabled="previewLoading" @change="loadPreview(1)">
            <option value="all">{{ $t('All history') }}</option><option value="eligible">{{ $t('Eligible unpaid only') }}</option>
          </select></label>
          <label>{{ $t('Tier') }} <select v-model="previewTier" :disabled="previewLoading" @change="loadPreview(1)">
            <option value="first">{{ $t('First tier') }}</option><option value="second">{{ $t('Second tier') }}</option>
          </select></label>
          <label>{{ $t('Type') }} <select v-model="previewKind" :disabled="previewLoading" @change="loadPreview(1)">
            <option value="transactions">{{ $t('Transactions') }}</option><option value="assists">{{ $t('Assisted commissions') }}</option>
          </select></label>
        </div>
        <p v-if="previewLoading" role="status">{{ $t('Loading commissions…') }}</p>
        <p v-else-if="previewError" role="alert">{{ previewError }} <button type="button" @click="loadPreview(previewPage)">{{ $t('Retry') }}</button></p>
        <template v-else-if="preview">
          <p>{{ preview.meta.total }} {{ preview.meta.scope === 'all' ? $t('historical records') : $t('eligible commissions') }}</p>
          <div class="commission-archive__table">
            <table v-if="preview.data.length">
              <thead><tr><th>{{ $t('Transaction') }}</th><th>{{ $t('Source funnel') }}</th><th>{{ $t('Date') }}</th><th>{{ $t('Type') }}</th><th>{{ $t('Commission status') }}</th><th>{{ $t('Commission') }}</th></tr></thead>
              <tbody><tr v-for="row in preview.data" :key="row.id">
                <td>{{ row.transaction_id }}</td><td>{{ row.funnel_name }}</td><td>{{ formatDate(row.created_timestamp) }}</td>
                <td>{{ transactionStatus(row) }}</td><td>{{ commissionStatus(row) }}</td><td>{{ formatCommission(row.commission, row.currency) }}</td>
              </tr></tbody>
            </table>
            <p v-else>{{ preview.meta.scope === 'all' ? $t('No pre-2025 history in this category.') : $t('No eligible pre-2025 commissions in this category.') }}</p>
          </div>
          <button type="button" :disabled="previewPage <= 1" @click="loadPreview(previewPage - 1)">{{ $t('Previous') }}</button>
          <span>{{ $t('Page') }} {{ previewPage }}</span>
          <button type="button" :disabled="previewPage * preview.meta.per_page >= preview.meta.total" @click="loadPreview(previewPage + 1)">{{ $t('Next') }}</button>
        </template>
      </div>
    </template>
    <button v-else-if="error" type="button" :disabled="loading" @click="loadArchive">{{ $t('Retry') }}</button>
  </section>
</template>
<script>
import axios from 'axios';

export default {
  props: { affiliateId: { type: [Number, String], required: true } },
  data() { return { archive: null, loading: false, saving: false, error: '', showPreview: false,
    preview: null, previewLoading: false, previewError: '', previewScope: 'all', previewTier: 'first', previewKind: 'transactions', previewPage: 1, previewGeneration: 0 }; },
  created() { this.loadArchive(); },
  watch: { affiliateId() { this.showPreview = false; this.preview = null; this.previewGeneration += 1; this.previewLoading = false; this.archive = null; this.loadArchive(); } },
  methods: {
    async togglePreview() {
      this.showPreview = !this.showPreview;
      if (this.showPreview) await this.loadPreview(1);
      else { this.previewGeneration += 1; this.previewLoading = false; }
    },
    async loadPreview(page = 1) {
      const generation = ++this.previewGeneration;
      const affiliateId = this.affiliateId;
      const scope = this.previewScope;
      this.previewPage = page;
      this.previewLoading = true;
      this.previewError = '';
      this.preview = null;
      try {
        const response = await axios.get(`/affiliates/${affiliateId}/commission-archive/transactions`, {
          params: { scope, tier: this.previewTier, kind: this.previewKind, page, per_page: 25 }
        });
        if (scope === 'all' && (!response.data.meta || response.data.meta.scope !== 'all')) {
          throw new Error('Full history response required');
        }
        if (generation === this.previewGeneration && affiliateId === this.affiliateId) this.preview = response.data;
      } catch (error) {
        if (generation === this.previewGeneration) this.previewError = this.$t('Unable to load commissions. Please retry.');
      } finally { if (generation === this.previewGeneration) this.previewLoading = false; }
    },
    formatCommission(amount, currency) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
    },
    formatDate(timestamp) {
      return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(timestamp * 1000));
    },
    transactionStatus(row) {
      return this.$t(({ 2: 'Payment', 3: 'Failed rebill', 4: 'Refund', 5: 'Cancellation' })[row.transaction_status] || 'Unknown');
    },
    commissionStatus(row) {
      if (row.is_refunded) return this.$t('Refunded');
      return this.$t(({ 1: 'Pending', 2: 'Payment due', 3: 'Paid', 4: 'Payment in progress' })[row.status] || 'Unknown');
    },
    async loadArchive() {
      const affiliateId = this.affiliateId;
      this.loading = true;
      this.error = '';
      try {
        const response = await axios.get(`/affiliates/${affiliateId}/commission-archive`);
        if (affiliateId !== this.affiliateId) return;
        this.archive = response.data;
      } catch (error) {
        if (affiliateId === this.affiliateId) this.error = this.$t('Unable to load archive settings. Please retry.');
      } finally { if (affiliateId === this.affiliateId) this.loading = false; }
    },
    async toggleArchive() {
      if (!this.archive || !this.archive.enabled || this.saving || this.loading) return;
      const affiliateId = this.affiliateId;
      const expectedRestored = !this.archive.restored;
      this.saving = true;
      this.error = '';
      try {
        const response = await axios.put(`/affiliates/${affiliateId}/commission-archive`, { restored: expectedRestored });
        if (affiliateId !== this.affiliateId) return;
        this.archive = response.data;
        this.$emit('updated');
      } catch (error) {
        // A response can be lost after the server committed; read back before retrying.
        if (affiliateId !== this.affiliateId) return;
        this.archive = null;
        await this.loadArchive();
        if (this.archive && this.archive.restored === expectedRestored) this.$emit('updated');
        else if (this.archive) this.error = this.$t('The archive was not changed. Please try again.');
      } finally { this.saving = false; }
    }
  }
};
</script>
<style scoped>
.commission-archive { margin: 0 20px 20px; padding: 16px; border: 1px solid #e3e8ef; border-radius: 10px; background: #fff; }
.commission-archive h4 { margin: 0 0 8px; color: #334155; font-size: 14px; font-weight: 600; }
.commission-archive p { margin: 8px 0; font-size: 13px; color: #475569; }
.commission-archive button { margin-top: 8px; border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 12px; color: #334155; background: #f8fafc; }
.commission-archive__preview { margin-top: 16px; padding-top: 12px; border-top: 1px solid #e3e8ef; }
.commission-archive__filters { display: flex; flex-wrap: wrap; gap: 12px; margin: 12px 0; }
.commission-archive__filters label { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.commission-archive__filters select { padding: 6px; border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; }
.commission-archive__table { overflow-x: auto; margin: 12px 0; }
.commission-archive__table table { width: 100%; border-collapse: collapse; font-size: 13px; }
.commission-archive__table th, .commission-archive__table td { padding: 10px; text-align: left; border-bottom: 1px solid #e3e8ef; }
.commission-archive__table th { background: #f8fafc; }
.commission-archive__table td:last-child { white-space: nowrap; }
.commission-archive button + button, .commission-archive__preview span { margin-left: 8px; }
.commission-archive button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.commission-archive button:disabled { opacity: .6; cursor: default; }
</style>
